import React, { useEffect, useState } from 'react';
import { Button, Form, Input, message, Row, Select, TreeSelect } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import I18N from 'src/services/I18NService';
import { TreeNode } from 'antd/lib/tree-select';
import DepartmentAdminService, { StaffAuthDict, StaffRoleDict } from 'src/services/DepartmentAdminService';
import { IStaffDetail, StaffAuths, StaffRoles } from 'src/entity/DepartmentEntity';


const { Option } = Select;

interface IProps extends FormComponentProps<{}> {
  formStatus: 'add' | 'edit',
  departmentTree: TreeNode[],
  user: string | undefined,
  departmentId: string | null,
  onFinished: () => void,
}

const defaultUserDetail: IStaffDetail = {
  uuid: '',
  code: '',
  department: '',
  username: '',
  fullname: '',
  mobile: '',
  password: '',
  role: StaffRoles.Salesman,
  is_active: true,
  data_auth: StaffAuths.Individual,
  email: '',
}

const formItemLayout = {
  labelCol: {
    span: 6
  },
  wrapperCol: {
    span: 8
  },
};

const DepartmentUserFormComp: React.FC<IProps> = (props) => {
  const {
    form,
    user,
    departmentId,
    departmentTree,
    formStatus,
    onFinished,
  } = props;
  const { getFieldDecorator } = form;

  const [loading, setLoading] = useState(false);
  const [userDetail, setUserDetail] = useState(() => {
    const defaultDepartmentId = departmentTree[0] ? departmentTree[0].value as string : '';
    return {
      ...defaultUserDetail,
      department: formStatus === 'add' ? (departmentId ? departmentId : defaultDepartmentId) : defaultDepartmentId,
    } as IStaffDetail
  });

  useEffect(() => {
    if (formStatus === 'edit' && user) {
      // 编辑人员时 => 通过 user 获取人员详情
      setLoading(true);
      DepartmentAdminService.fetchDepartmentStaffDetail(user).then(
        res => {
          if (res) {
            setUserDetail(res);
          }
        },
      ).finally(() => setLoading(false));
    }
  }, [formStatus, user]);

  useEffect(() => {
    if (formStatus === 'add' && departmentTree[0]) {
      const defaultDepartmentId = departmentTree[0].value as string;
      setUserDetail({
        ...defaultUserDetail,
        department: departmentId ? departmentId : defaultDepartmentId,
      })
    }
  }, [formStatus, departmentTree])

  const handleSubmit = () => {
    form.validateFields(async (err, values: IStaffDetail) => {
      if (!err) {
        const {
          department,
          username,
          fullname,
          password,
          mobile,
          role,
          data_auth,
        } = values;
        const params: Partial<IStaffDetail> = {
          username,
          fullname,
          department,
          password,
          mobile,
          role,
          data_auth,
        };
        setLoading(true);
        let res: IStaffDetail | undefined;
        if (formStatus === "edit" && user) {
          // 编辑人员
          params.uuid = user;
          delete params.password;
          delete params.username;
          delete params.role;
          res = await DepartmentAdminService.updateDepartmentStaff(params, user);
        } else if (formStatus === "add") {
          // 新增人员
          res = await DepartmentAdminService.createDepartmentStaff(params);
        }
        setLoading(false);
        if (res) {
          message.success(I18N.get('common_submit_success'));
          onFinished();
        }
      }
    })
  };

  return (
    <Form className="mdd-search-form" {...formItemLayout} style={{width: 500, margin: '0 auto'}}>
      <Form.Item
        label={I18N.get('login_userName')}
      >
        {getFieldDecorator('username', {
          initialValue: userDetail.username,
          rules: [
            {
              required: true,
              message: `${I18N.get('common_input')}${I18N.get('login_userName')}`
            }
          ]
        })(
          <Input
            autoComplete="off"
            disabled={formStatus === 'edit'}
            placeholder={`${I18N.get('common_input')}${I18N.get('login_userName')}`}
          />
        )}
      </Form.Item>
      <Form.Item
        label={I18N.get('common_name')}
      >
        {getFieldDecorator('fullname', {
          initialValue: userDetail.fullname,
          rules: [
            {
              required: true,
              message: `${I18N.get('common_input')}${I18N.get('common_name')}`
            }
          ]
        })(
          <Input
            autoComplete="off"
            placeholder={`${I18N.get('common_input')}${I18N.get('common_name')}`}
          />
        )}
      </Form.Item>
      <Form.Item
        label={I18N.get('common_mobile')}
      >
        {getFieldDecorator('mobile', {
          initialValue: userDetail.mobile,
          rules: [
            {
              required: true,
              message: `${I18N.get('common_input')}${I18N.get('common_mobile')}`
            },
            {
              pattern: /^((13)|(14)|(15)|(16)|(17)|(18)|(19))\d{9}$/,
              message: `${I18N.get('err_mobile')}`,
            },
          ],
        })(
          <Input
            autoComplete="off"
            placeholder={`${I18N.get('common_input')}${I18N.get('common_mobile')}`}
          />
        )}
      </Form.Item>
      {formStatus === 'add' && (
        <Form.Item
          label={I18N.get('login_pwd')}
        >
          {getFieldDecorator('password', {
            initialValue: userDetail.password,
            rules: [
              {
                required: true,
                message: `${I18N.get('common_input')}${I18N.get('login_pwd')}`
              },
              {
                min: 8,
                max: 20,
                message: `${I18N.get('login_pwd_require')}`,
              },
            ]
          })(
            <Input
              autoComplete="off"
              placeholder={`${I18N.get('common_input')}${I18N.get('login_pwd')}(${I18N.get('login_pwd_require')})`}
            />
          )}
        </Form.Item>
      )}
      <Form.Item
        label={I18N.get('departmentAdmin_department')}
      >
        {getFieldDecorator('department', {
          initialValue: userDetail.department,
          rules: [
            {
              required: true,
              message: `${I18N.get('common_choose')}${I18N.get('departmentAdmin_department')}`
            }
          ]
        })(
          <TreeSelect
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            allowClear={true}
            treeData={departmentTree}
            treeDefaultExpandAll={true}
            placeholder={`${I18N.get('common_choose')}${I18N.get('departmentAdmin_department')}`}
          />
        )}
      </Form.Item>
      <Form.Item label={I18N.get('userAdmin_role')}>
        {getFieldDecorator('role', {
          initialValue: userDetail.role,
          rules: [
            {
              required: true,
              message: `${I18N.get('common_choose')}${I18N.get('userAdmin_role')}`
            }
          ]
        })(
          <Select
            disabled={formStatus === 'edit'}
            allowClear={true}
            placeholder={`${I18N.get('common_choose')}${I18N.get('userAdmin_role')}`}
          >
            {StaffRoleDict.map(item => (
              <Option key={String(item.value)} value={String(item.value)}>{I18N.get(item.key)}</Option>
            ))}
          </Select>
          
        )}
      </Form.Item>
      <Form.Item label={I18N.get('departmentAdmin_data_auth')}>
        {getFieldDecorator('data_auth', {
          initialValue: userDetail.data_auth,
          rules: [
            {
              required: true,
              message: `${I18N.get('common_choose')}${I18N.get('departmentAdmin_data_auth')}`
            }
          ]
        })(
          <Select
            allowClear={true}
            placeholder={`${I18N.get('common_choose')}${I18N.get('departmentAdmin_data_auth')}`}
          >
            {StaffAuthDict.map(item => (
              <Option key={String(item.value)} value={String(item.value)}>{I18N.get(item.key)}</Option>
            ))}
          </Select>
          
        )}
      </Form.Item>
      <Row type="flex" justify="center" style={{marginTop: 30}}>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={loading}
        >{I18N.get('common_ok')}</Button>
        <Button
          onClick={onFinished}
          style={{marginLeft: 16}}
        >{I18N.get('common_cancel')}</Button>
      </Row>
    </Form>
  )
}

const WrappedDepartmentUserForm = Form.create<IProps>()(DepartmentUserFormComp);
const DepartmentUserForm =  React.memo(WrappedDepartmentUserForm);
export default DepartmentUserForm;


// WEBPACK FOOTER //
// ./src/components/views/DepartmentAdmin/User/UserForm.tsx